/**
 * Recently Viewed Page Component
 * Displays all recently viewed products with infinite scroll pagination
 * Handles localStorage, mimicking the paginated-list UX
 */

const STORAGE_KEY = 'theme:recently-viewed-products';
const AUTO_LOAD_LIMIT = 3; // Auto-load 3 pages, then show "Load More" button

/**
 * Get the locale-aware search URL
 */
function getSearchUrl() {
  if (window.theme?.routes?.search_url) {
    return window.theme.routes.search_url;
  }

  const pathname = window.location.pathname;
  const localeMatch = pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/);

  if (localeMatch) {
    return `/${localeMatch[1]}/search`;
  }

  return '/search';
}

class RecentlyViewedPage extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.currentPage = -1; // Start at -1 so first increment brings us to 0
    this.allHandles = [];
    this.productsPerPage = 16;
    this.autoLoadedPagesCount = 0;
    this.shouldObserveNext = true;
    this.infinityScrollObserver = null;
  }

  connectedCallback() {
    this.productsPerPage = parseInt(this.getAttribute('data-per-page')) || 16;
    this.sectionId = this.getAttribute('data-section-id');

    // Get elements
    this.grid = this.querySelector('.recently-viewed-page__grid');
    this.viewMoreTrigger = this.querySelector('.recently-viewed-page__view-more-trigger');
    this.loadMoreWrapper = this.querySelector('.load-more-wrapper');
    this.loadMoreButton = this.loadMoreWrapper?.querySelector('.load-more-button');
    this.emptyState = this.querySelector('.recently-viewed-page__empty');

    // Load all handles
    this.allHandles = this.getRecentlyViewedHandles();

    if (this.allHandles.length === 0) {
      this.showEmptyState();
      return;
    }

    // Load first page
    this.renderNextPage();

    // Setup infinite scroll observer
    this.#observeViewMore();
  }

  disconnectedCallback() {
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
  }

  /**
   * Get recently viewed product handles from localStorage
   */
  getRecentlyViewedHandles() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return [];
      }

      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('[RecentlyViewed] Error reading from localStorage:', error);
      return [];
    }
  }

  /**
   * Calculate total pages
   */
  getTotalPages() {
    return Math.ceil(this.allHandles.length / this.productsPerPage);
  }

  /**
   * Get handles for a specific page (0-indexed)
   */
  getPageHandles(page) {
    const startIndex = page * this.productsPerPage;
    const endIndex = startIndex + this.productsPerPage;
    return this.allHandles.slice(startIndex, endIndex);
  }

  /**
   * Check if there are more pages to load
   */
  hasNextPage() {
    return this.currentPage < this.getTotalPages() - 1;
  }

  /**
   * Setup IntersectionObserver for infinite scroll
   */
  #observeViewMore() {
    if (!this.viewMoreTrigger) return;

    // Create observer
    this.infinityScrollObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && this.shouldObserveNext) {
            this.renderNextPage();
          }
        }
      },
      {
        rootMargin: '100px',
      }
    );

    // Observe the trigger
    if (this.shouldObserveNext) {
      this.infinityScrollObserver.observe(this.viewMoreTrigger);
    }
  }

  /**
   * Render the next page of products
   */
  async renderNextPage() {
    if (this.loading || !this.hasNextPage()) {
      return;
    }

    this.loading = true;
    this.currentPage++;

    try {
      // Get handles for this page
      const pageHandles = this.getPageHandles(this.currentPage);

      if (pageHandles.length === 0) {
        return;
      }

      // Only show skeleton loader for pages after the first
      // (First page uses server-rendered skeletons)
      if (this.currentPage > 0) {
        this.#showSkeletonLoader();
      }

      // Fetch and render products
      await this.#fetchAndAppendProducts(pageHandles);

      // Hide skeleton loader (only affects JS-added skeletons)
      this.#hideSkeletonLoader();

      // Increment auto-load counter
      this.autoLoadedPagesCount++;

      // Check if we should show "Load More" button
      if (this.autoLoadedPagesCount > 0 && this.autoLoadedPagesCount % AUTO_LOAD_LIMIT === 0) {
        // Stop auto-loading
        this.shouldObserveNext = false;

        // Unobserve trigger
        if (this.viewMoreTrigger && this.infinityScrollObserver) {
          this.infinityScrollObserver.unobserve(this.viewMoreTrigger);
        }

        // Show Load More button
        this.#showLoadMoreButton();
      }

    } catch (error) {
      console.error('[RecentlyViewed] Error rendering next page:', error);
      this.#hideSkeletonLoader();
    } finally {
      this.loading = false;
    }
  }

  /**
   * Fetch products and append to grid
   */
  async #fetchAndAppendProducts(handles) {
    try {
      // Build search query
      const searchQuery = handles.map(handle => `handle:${handle}`).join(' OR ');

      // Fetch rendered section using search endpoint
      const searchUrl = getSearchUrl();
      const url = `${searchUrl}?type=product&q=${encodeURIComponent(searchQuery)}&section_id=${encodeURIComponent(this.sectionId)}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch section HTML');
      }

      const html = await response.text();

      // Parse the response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find the product grid
      const productGrid = doc.querySelector('.recently-viewed-page__grid');

      if (!productGrid) {
        throw new Error('Product grid not found in response');
      }

      // Get product cards and sort them
      const items = Array.from(productGrid.querySelectorAll('.resource-list__item'));
      const sortedItems = this.#sortProductCards(items, handles);

      if (!this.grid || sortedItems.length === 0) {
        return;
      }

      // On first page, replace server-rendered skeletons
      if (this.currentPage === 0) {
        this.grid.innerHTML = '';
      }

      // Append to grid
      this.grid.append(...sortedItems);

      // Reinitialize Rivo buttons
      requestAnimationFrame(() => {
        this.#reinitializeRivoButtons();
      });

    } catch (error) {
      console.error('[RecentlyViewed] Error fetching products:', error);
      throw error;
    }
  }

  /**
   * Sort product cards to match handle order
   */
  #sortProductCards(items, handles) {
    // Create map of handle -> element
    const cardMap = new Map();
    items.forEach(item => {
      const link = item.querySelector('a[href*="/products/"]');
      if (link) {
        const href = link.getAttribute('href');
        const match = href.match(/\/products\/([^/?]+)/);
        if (match) {
          cardMap.set(match[1], item);
        }
      }
    });

    // Build sorted array
    const sortedItems = [];
    for (const handle of handles) {
      const item = cardMap.get(handle);
      if (item) {
        sortedItems.push(item);
      }
    }

    return sortedItems;
  }

  /**
   * Show skeleton loading indicator
   */
  #showSkeletonLoader() {
    if (!this.grid) return;

    const skeletonCards = Array.from({ length: this.productsPerPage }, () => {
      const skeletonItem = document.createElement('div');
      skeletonItem.className = 'resource-list__item';
      skeletonItem.setAttribute('data-skeleton-item', 'true');
      skeletonItem.innerHTML = `
        <div class="product-card-skeleton">
          <div class="product-card-skeleton__image"></div>
          <div class="product-card-skeleton__content">
            <div class="product-card-skeleton__title"></div>
            <div class="product-card-skeleton__price"></div>
          </div>
        </div>
      `;
      return skeletonItem;
    });

    skeletonCards.forEach(card => this.grid.appendChild(card));
  }

  /**
   * Hide skeleton loading indicator
   */
  #hideSkeletonLoader() {
    const skeletonItems = this.querySelectorAll('[data-skeleton-item="true"]');
    skeletonItems.forEach(item => {
      item.classList.add('is-removing');
      setTimeout(() => item.remove(), 300);
    });
  }

  /**
   * Show the Load More button
   */
  #showLoadMoreButton() {
    if (!this.loadMoreWrapper || !this.loadMoreButton) return;

    // Check if there are more pages
    if (!this.hasNextPage()) {
      this.loadMoreWrapper.style.display = 'none';
      return;
    }

    // Show button
    this.loadMoreWrapper.style.display = '';
    this.loadMoreButton.disabled = false;

    // Setup click handler
    const handleClick = async () => {
      // Disable button
      this.loadMoreButton.disabled = true;

      // Hide button
      this.loadMoreWrapper.style.display = 'none';

      // Load next page
      await this.renderNextPage();

      // Reset counter to start new auto-load cycle
      this.autoLoadedPagesCount = 0;

      // Re-enable auto-loading
      this.shouldObserveNext = true;

      // Re-observe trigger
      if (this.viewMoreTrigger && this.infinityScrollObserver) {
        this.infinityScrollObserver.observe(this.viewMoreTrigger);
      }

      // Remove click handler
      this.loadMoreButton.removeEventListener('click', handleClick);
    };

    // Add click handler
    this.loadMoreButton.addEventListener('click', handleClick);
  }

  /**
   * Show empty state
   */
  showEmptyState() {
    if (this.grid) {
      this.grid.style.display = 'none';
    }
    if (this.loadMoreWrapper) {
      this.loadMoreWrapper.style.display = 'none';
    }
    if (this.emptyState) {
      this.emptyState.style.display = 'block';
    }
  }

  /**
   * Reinitialize Rivo favorite buttons
   */
  #reinitializeRivoButtons() {
    try {
      if (window.RivoProfileAlpine) {
        const rivoAccountWidget = document.getElementById('rivo-account-slideout');
        if (rivoAccountWidget) {
          window.RivoProfileAlpine.$data(rivoAccountWidget).initializeFavoriteButtons();
        }
      }
    } catch (error) {
      console.error('[RecentlyViewed] Error reinitializing Rivo buttons:', error);
    }
  }
}

// Register custom element
if (!customElements.get('recently-viewed-page')) {
  customElements.define('recently-viewed-page', RecentlyViewedPage);
}
